import React, { useEffect, useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import Select from 'react-select';
import { FiActivity, FiFilter, FiCalendar, FiUser, FiLayers, FiDownload } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { useWorkspace } from '../context/WorkspaceContext';
import Sidebar from '../components/Layout/Sidebar';
import Header from '../components/Layout/Header';

const pageStyles = {
  pageContainer: {
    marginLeft: '72px',
    minHeight: '100vh',
    background: '#f8fafc',
  },
  mainContent: {
    marginTop: '64px',
    padding: '32px',
  },
  pageHeader: {
    marginBottom: '32px',
  },
  pageTitle: {
    fontSize: '1.875rem',
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  pageSubtitle: {
    fontSize: '0.95rem',
    color: '#64748b',
  },
  filterCard: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1e293b',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '20px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  filterLabel: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  filterInput: {
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.875rem',
    color: '#1e293b',
    outline: 'none',
    transition: 'all 0.2s ease',
  },
  filterActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  exportButton: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: 'auto',
  },
  applyButton: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  resetButton: {
    padding: '10px 20px',
    background: 'white',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  logsCard: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '24px',
  },
  logsHeader: {
    marginBottom: '20px',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1e293b',
  },
  logsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  logItem: {
    padding: '16px',
    background: '#fafbfc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  logBadges: {
    display: 'flex',
    gap: '8px',
    marginBottom: '8px',
    flexWrap: 'wrap',
  },
  badge: {
    padding: '4px 12px',
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: '600',
    display: 'inline-block',
  },
  logContent: {
    flex: 1,
  },
  logDescription: {
    fontSize: '0.9375rem',
    color: '#1e293b',
    marginBottom: '4px',
    lineHeight: '1.5',
  },
  logComment: {
    fontSize: '0.875rem',
    color: '#64748b',
    fontStyle: 'italic',
    marginTop: '8px',
    paddingLeft: '12px',
    borderLeft: '2px solid #e2e8f0',
  },
  logMeta: {
    fontSize: '0.8125rem',
    color: '#94a3b8',
    marginTop: '6px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#94a3b8',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '16px',
    opacity: 0.3,
  },
  emptyText: {
    fontSize: '1rem',
    fontWeight: '500',
  },
};

const actionStyles = {
  created: { color: '#10b981', bg: '#d1fae5' },
  updated: { color: '#3b82f6', bg: '#dbeafe' },
  deleted: { color: '#ef4444', bg: '#fee2e2' },
  status_changed: { color: '#8b5cf6', bg: '#ede9fe' },
  commented: { color: '#f59e0b', bg: '#fef3c7' },
  assigned: { color: '#06b6d4', bg: '#cffafe' },
};

const resourceStyles = {
  change_request: { label: 'Change Request', color: '#6366f1', bg: '#eef2ff' },
  template: { label: 'Template', color: '#ec4899', bg: '#fce7f3' },
};

// Custom styles for React Select
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.875rem',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.1)' : 'none',
    '&:hover': {
      border: '1px solid #cbd5e1',
    },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected
      ? '#6366f1'
      : state.isFocused
      ? '#f1f5f9'
      : 'white',
    color: state.isSelected ? 'white' : '#1e293b',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#6366f1',
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#94a3b8',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#1e293b',
  }),
};

const AuditLogs = () => {
  const { currentWorkspace, auditLogs, members, workspaces, fetchWorkspaces, fetchWorkspace, fetchAuditLogs, fetchMembers } = useWorkspace();

  const [filters, setFilters] = useState({
    resource_type: '',
    action: '',
    user_id: '',
    start_date: '',
    end_date: '',
  });

  const [loading, setLoading] = useState(false);

  // Options for React Select dropdowns
  const resourceTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'change_request', label: 'Change Requests' },
    { value: 'template', label: 'Templates' },
  ];

  const actionOptions = [
    { value: '', label: 'All Actions' },
    { value: 'created', label: 'Created' },
    { value: 'updated', label: 'Updated' },
    { value: 'deleted', label: 'Deleted' },
    { value: 'status_changed', label: 'Status Changed' },
    { value: 'commented', label: 'Commented' },
    { value: 'assigned', label: 'Assigned' },
  ];

  const userOptions = [
    { value: '', label: 'All Users' },
    ...members.map((member) => ({
      value: member.user_id,
      label: member.user_name,
    })),
  ];

  useEffect(() => {
    const initializePage = async () => {
      try {
        const existingWorkspaces = await fetchWorkspaces();
        if (existingWorkspaces && existingWorkspaces.length > 0) {
          const workspaceId = existingWorkspaces[0].id;
          await fetchWorkspace(workspaceId);
          await fetchMembers(workspaceId);
          await loadLogsForWorkspace(workspaceId);
        }
      } catch (error) {
        console.error('Failed to initialize audit logs page:', error);
      }
    };

    initializePage();
  }, []);

  const loadLogsForWorkspace = async (workspaceId) => {
    setLoading(true);
    try {
      const activeFilters = {};
      if (filters.resource_type) activeFilters.resource_type = filters.resource_type;
      if (filters.action) activeFilters.action = filters.action;
      if (filters.user_id) activeFilters.user_id = filters.user_id;
      if (filters.start_date) activeFilters.start_date = filters.start_date;
      if (filters.end_date) activeFilters.end_date = filters.end_date;

      await fetchAuditLogs(workspaceId, activeFilters);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    if (currentWorkspace) {
      await loadLogsForWorkspace(currentWorkspace.id);
    }
  };

  const handleFilterChange = (field, selectedOption) => {
    setFilters(prev => ({ ...prev, [field]: selectedOption ? selectedOption.value : '' }));
  };

  const handleDateChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    loadLogs();
  };

  const handleResetFilters = () => {
    setFilters({
      resource_type: '',
      action: '',
      user_id: '',
      start_date: '',
      end_date: '',
    });
    setTimeout(() => {
      if (currentWorkspace) {
        fetchAuditLogs(currentWorkspace.id, {});
      }
    }, 100);
  };

  const getActionStyle = (action) => {
    return actionStyles[action] || { color: '#64748b', bg: '#f1f5f9' };
  };

  const getResourceStyle = (resourceType) => {
    return resourceStyles[resourceType] || { label: resourceType, color: '#64748b', bg: '#f1f5f9' };
  };

  const handleExportExcel = () => {
    if (auditLogs.length === 0) return;

    const rows = auditLogs.map((log) => ({
      'Date & Time': format(new Date(log.created_at), 'dd MMM yyyy, hh:mm a'),
      'User': log.user_name,
      'Action': log.action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      'Resource Type': log.resource_type === 'change_request' ? 'Change Request' : 'Template',
      'Resource Name': log.resource_name,
      'Details': log.new_value || '',
      'Previous Value': log.old_value || '',
      'Comment': log.comment || '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    // Column widths
    ws['!cols'] = [
      { wch: 22 }, { wch: 20 }, { wch: 18 }, { wch: 16 },
      { wch: 40 }, { wch: 45 }, { wch: 20 }, { wch: 45 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Audit Logs');

    const workspaceName = currentWorkspace?.name || 'workspace';
    const dateStr = format(new Date(), 'yyyy-MM-dd');
    XLSX.writeFile(wb, `audit-logs-${workspaceName}-${dateStr}.xlsx`);
  };

  return (
    <>
      <Sidebar workspace={currentWorkspace} />
      <Header workspace={currentWorkspace} />
      <div style={pageStyles.pageContainer}>
        <main style={pageStyles.mainContent}>
          <div style={pageStyles.pageHeader}>
            <h1 style={pageStyles.pageTitle}>
              <FiActivity size={32} />
              Audit Logs
            </h1>
            <p style={pageStyles.pageSubtitle}>
              Track all activities and changes in your workspace
            </p>
          </div>

          <div style={pageStyles.filterCard}>
            <div style={pageStyles.filterHeader}>
              <FiFilter size={18} />
              Filters
            </div>
            <div style={pageStyles.filterGrid}>
              <div style={pageStyles.filterGroup}>
                <label style={pageStyles.filterLabel}>
                  <FiLayers size={14} />
                  Resource Type
                </label>
                <Select
                  options={resourceTypeOptions}
                  value={resourceTypeOptions.find(opt => opt.value === filters.resource_type)}
                  onChange={(option) => handleFilterChange('resource_type', option)}
                  styles={selectStyles}
                  placeholder="All Types"
                  isClearable
                  isSearchable
                />
              </div>

              <div style={pageStyles.filterGroup}>
                <label style={pageStyles.filterLabel}>
                  <FiActivity size={14} />
                  Action
                </label>
                <Select
                  options={actionOptions}
                  value={actionOptions.find(opt => opt.value === filters.action)}
                  onChange={(option) => handleFilterChange('action', option)}
                  styles={selectStyles}
                  placeholder="All Actions"
                  isClearable
                  isSearchable
                />
              </div>

              <div style={pageStyles.filterGroup}>
                <label style={pageStyles.filterLabel}>
                  <FiUser size={14} />
                  User
                </label>
                <Select
                  options={userOptions}
                  value={userOptions.find(opt => opt.value === filters.user_id)}
                  onChange={(option) => handleFilterChange('user_id', option)}
                  styles={selectStyles}
                  placeholder="All Users"
                  isClearable
                  isSearchable
                />
              </div>

              <div style={pageStyles.filterGroup}>
                <label style={pageStyles.filterLabel}>
                  <FiCalendar size={14} />
                  Start Date
                </label>
                <input
                  type="date"
                  style={pageStyles.filterInput}
                  value={filters.start_date}
                  onChange={(e) => handleDateChange('start_date', e.target.value)}
                />
              </div>

              <div style={pageStyles.filterGroup}>
                <label style={pageStyles.filterLabel}>
                  <FiCalendar size={14} />
                  End Date
                </label>
                <input
                  type="date"
                  style={pageStyles.filterInput}
                  value={filters.end_date}
                  onChange={(e) => handleDateChange('end_date', e.target.value)}
                />
              </div>
            </div>

            <div style={pageStyles.filterActions}>
              <button
                style={pageStyles.applyButton}
                onClick={handleApplyFilters}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-1px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Apply Filters
              </button>
              <button
                style={pageStyles.resetButton}
                onClick={handleResetFilters}
                onMouseOver={(e) => e.target.style.background = '#f8fafc'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                Reset
              </button>
              <button
                style={{
                  ...pageStyles.exportButton,
                  opacity: auditLogs.length === 0 ? 0.5 : 1,
                  cursor: auditLogs.length === 0 ? 'not-allowed' : 'pointer',
                }}
                onClick={handleExportExcel}
                disabled={auditLogs.length === 0}
                onMouseOver={(e) => { if (auditLogs.length > 0) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <FiDownload size={15} />
                Export Excel
              </button>
            </div>
          </div>

          <div style={pageStyles.logsCard}>
            <div style={pageStyles.logsHeader}>
              Activity Log ({auditLogs.length} {auditLogs.length === 1 ? 'entry' : 'entries'})
            </div>

            {loading ? (
              <div style={pageStyles.emptyState}>
                <div style={pageStyles.emptyText}>Loading audit logs...</div>
              </div>
            ) : auditLogs.length === 0 ? (
              <div style={pageStyles.emptyState}>
                <div style={pageStyles.emptyIcon}>
                  <FiActivity />
                </div>
                <div style={pageStyles.emptyText}>No audit logs found</div>
              </div>
            ) : (
              <div style={pageStyles.logsList}>
                {auditLogs.map((log) => {
                  const actionStyle = getActionStyle(log.action);
                  const resourceStyle = getResourceStyle(log.resource_type);

                  return (
                    <div key={`${log.resource_type}-${log.id}`} style={pageStyles.logItem}>
                      <div style={pageStyles.logContent}>
                        <div style={pageStyles.logBadges}>
                          <span
                            style={{
                              ...pageStyles.badge,
                              color: actionStyle.color,
                              background: actionStyle.bg,
                            }}
                          >
                            {log.action.replace('_', ' ')}
                          </span>
                          <span
                            style={{
                              ...pageStyles.badge,
                              color: resourceStyle.color,
                              background: resourceStyle.bg,
                            }}
                          >
                            {resourceStyle.label}
                          </span>
                        </div>
                        <div style={pageStyles.logDescription}>
                          <strong>{log.user_name}</strong> {log.new_value || log.action}
                          {log.old_value && ` (from: ${log.old_value})`}
                        </div>
                        {log.comment && (
                          <div style={pageStyles.logComment}>
                            "{log.comment}"
                          </div>
                        )}
                        <div style={pageStyles.logMeta}>
                          {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })} • {log.resource_name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AuditLogs;
